import React from 'react';
import styled, { TC } from '@benzinga/themetron';
import { Skeleton } from 'antd';
import { StockSymbol } from '@benzinga/session';
import { MarketingWin } from '@benzinga/marketing-wins-manager';
import { formatDate } from '@benzinga/date-utils';
import {
  HorizontalRuleModule,
  ProTickerPopover,
  SparkLineModule,
  TickerHeaderModule,
  TickerNotesModule,
} from '../TickerHover';
import { TickerColor } from '../Ticker';
import { TopStock } from '@benzinga/top-stocks-manager';
import { faTrophy } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '@benzinga/core-ui';

interface Props {
  win?: MarketingWin;
  onSymbolClick: (symbol: StockSymbol) => void;
}

export const MarketingWinItem: React.FC<Props> = ({ onSymbolClick, win }) => {
  if (win === undefined) {
    return <Skeleton active loading={win === undefined} />;
  } else {
    return (
      <NewsItem>
        <Item>
          <TextHolder>
            <div>
              <Title>{win.theWin}</Title>
              <div>
                {`${
                  win.name
                    ? `User ${win.name} `
                    : `${win.tradeType ? `${win.tradeType} Trader traded` : 'Trader traded'}`
                } `}
                <ProTickerPopover
                  modules={
                    <>
                      <TickerHeaderModule onSymbolClick={onSymbolClick} />
                      <HorizontalRuleModule />
                      <SparkLineModule referenceTime={win.date} />
                      <HorizontalRuleModule />
                      <TickerNotesModule />
                    </>
                  }
                  symbol={win.symbol}
                >
                  <TickerColor onSymbolClick={onSymbolClick} symbol={win.symbol} />
                </ProTickerPopover>
                {` ${win.date ? ` on ${formatDate(new Date(win.date))}` : ''}${
                  win.percentGain ? ` resulting in ${win.percentGain}% gain` : ''
                }`}
              </div>
            </div>
            <Body>{win.theWinDetailed}</Body>
          </TextHolder>
        </Item>
      </NewsItem>
    );
  }
};

export const TopWinItem: React.FC<{ win: TopStock }> = ({ win }) => {
  if (win === undefined) {
    return <Skeleton active loading={win === undefined} />;
  } else {
    return (
      <NewsItem>
        <Item topWin>
          <TextHolder>
            <Title>{win.theWin}</Title>
            <Body>{win.description}</Body>
          </TextHolder>
          <StyledIcon icon={faTrophy} title={'Top Win of the Week'} />
        </Item>
      </NewsItem>
    );
  }
};

const Title = styled.div`
  font-size: 16px;
  padding-bottom: 4px;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding-bottom: 30px;
`;

const NewsItem = styled.div`
  padding: 4px;
`;

const Item = styled(TC.Row)<{ topWin?: boolean }>`
  box-sizing: border-box;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 12px;
  gap: 8px;

  width: 100%;
  min-width: 290px;
  height: 120px;

  background: ${props =>
    props.topWin
      ? `linear-gradient(90deg, #faca1555 -69%, rgba(48, 191, 163, 0.00) 101.2%), ${props.theme.colors.backgroundActive}`
      : props.theme.colors.backgroundActive}88;
  border-left: 2px solid ${props => (props.topWin ? '#faca15' : `${props.theme.colors.brand}AA`)};

  flex: none;
  order: 0;
  flex-grow: 0;
`;

const TextHolder = styled(TC.Column)`
  flex-direction: column;
  gap: 8px;
  height: 100%;
`;

const StyledIcon = styled(Icon)`
  color: #faca15;
  font-size: 4em;
  display: flex;
  align-self: center;
`;
